import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router';
import { useAppointmentProposalFiles } from '../../../data-access/appointmentProposalFiles';
import { useAppointmentProposalOptionProducts } from '../../../data-access/appointmentProposalOptionProducts';
import { useAppointmentProposalOptions } from '../../../data-access/appointmentProposalOptions';
import { useAppointmentProposals } from '../../../data-access/appointmentProposals';
import { useProposalQualityScore } from '../../../data-access/proposalQualityScore';

export const useProposalScore = (): void => {
  const { appointmentId, appointmentProposalId } = useParams<{
    appointmentId: string;
    appointmentProposalId: string;
  }>();

  const { currentProposal, isLoaded: proIsLoaded } = useAppointmentProposals();
  const { options, isLoaded: apoIsLoaded } = useAppointmentProposalOptions();
  const { proposalProducts, isLoaded: appIsLoaded } =
    useAppointmentProposalOptionProducts();
  const { files } = useAppointmentProposalFiles();
  const { fetchProposalQualityScore } = useProposalQualityScore();

  const isLoaded = proIsLoaded && apoIsLoaded && appIsLoaded;
  const optionIds = new Set(options.map((o) => o.appointmentProposalOptionId));

  const itemsCounts =
    isLoaded && currentProposal
      ? [
          optionIds.size,
          files.filter((f) => f.metadata.uploadCategory === 'EXISTING_SYSTEM')
            .length,
          ...options.map((o) => (o.summary ? 1 : 0)),
          proposalProducts.filter((p) =>
            optionIds.has(p.appointmentProposalOptionId)
          ).length,
          currentProposal.customerExperience?.current?.length,
          currentProposal.customerExperience?.priorities?.length,
          currentProposal.selectedUpgradeIds?.length,
        ]
          .filter(Boolean)
          .join('|')
          .trim()
      : '';

  const qualityScoreHash = useMemo(
    () =>
      !itemsCounts
        ? ''
        : [
            currentProposal?.presentationConfig?.coverPhoto?.fileType,
            currentProposal?.presentationConfig?.headline,
            currentProposal?.presentationConfig?.subheadline,
            currentProposal?.presentationConfig?.summary,
            itemsCounts,
          ]
            .filter(Boolean)
            .join('|')
            .trim(),
    [
      currentProposal?.presentationConfig?.coverPhoto?.fileType,
      currentProposal?.presentationConfig?.headline,
      currentProposal?.presentationConfig?.subheadline,
      currentProposal?.presentationConfig?.summary,
      itemsCounts,
    ]
  );

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (appointmentId && appointmentProposalId && qualityScoreHash) {
        fetchProposalQualityScore(appointmentId, appointmentProposalId);
      }
    }, 3000);

    return () => clearTimeout(timeout);
  }, [
    appointmentId,
    appointmentProposalId,
    qualityScoreHash,
    fetchProposalQualityScore,
  ]);
};
